import Link from "next/link";
import { Knopf } from "./knopf";

/**
 * Leerzustand mit Symbol, Titel, Satz und optionalem Absprung (statt der zwei Knöpfe
 * des Originals höchstens ein Link im Stil des sekundären Knopfs).
 * Dient auch für „Kennzahl fehlt noch“ (sichtbar, keine erfundene Zahl).
 */
export function LeerZustand({
  titel,
  text,
  aktion,
  symbol,
  aktionsPlatz,
}: {
  titel: string;
  text: string;
  aktion?: { href: string; text: string };
  /** Eigenes Symbol (Lucide, vom Aufrufer) statt der Lupe, z. B. Büroklammer bei „Keine Anhänge“ (M-011 Teil 3). */
  symbol?: React.ReactNode;
  /** Platz für eine bedienbare Aktion statt des Links (z. B. „Datei anhängen“); nur mit Schreibrecht übergeben. */
  aktionsPlatz?: React.ReactNode;
}) {
  const hatAktion = Boolean(aktion) || Boolean(aktionsPlatz);
  return (
    <>
      {/* Preline: feedback-and-states/feedback-and-states-sub-alerts-recovery/feedback-and-states-cat-empty-states/no-events-empty-state-with-import-and-create-actions */}
      <div className="p-5 flex flex-col justify-center items-center text-center" role="status">
        <div className={hatAktion ? "mb-6 max-w-md mx-auto" : "max-w-md mx-auto"}>
          <div className="inline-flex size-8 items-center justify-center rounded-full bg-surface border border-surface-line text-surface-foreground">
            {symbol ?? (
              <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m21 21-4.34-4.34" />
                <circle cx="11" cy="11" r="8" />
              </svg>
            )}
          </div>
          <p className="mt-3 font-medium text-foreground">{titel}</p>
          <p className="mt-1 text-sm text-muted-foreground-1">{text}</p>
        </div>

        {aktionsPlatz && <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">{aktionsPlatz}</div>}
        {aktion && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <Knopf groesse="sm" href={aktion.href}>
              {aktion.text}
            </Knopf>
          </div>
        )}
      </div>
      {/* End Empty State */}
    </>
  );
}

/**
 * Schmale Leerzeile (PM einfach, Paket 1): ein leerer Nebenblock der Projekt-Übersicht (Teilprojekte, Wiki, …) steht nicht als
 * große leere Karte da, sondern als eine Zeile in Kartenrahmen: Titel, ein Satz, rechts höchstens eine Aktion. Der Aufrufer
 * rendert sie nur mit Schreibrecht (ohne Recht lässt er den Block weg, r110); die Aktion trägt dieselbe Prüfung wie die Action.
 */
export function LeerZeile({
  id,
  titel,
  text,
  aktion,
  aktionsPlatz,
}: {
  id?: string;
  titel: string;
  text: string;
  /** Ein Link zum Anlegen. */
  aktion?: { href: string; text: string };
  /** Eine bedienbare Aktion statt des Links (z. B. Knöpfe, die ein Fenster öffnen). */
  aktionsPlatz?: React.ReactNode;
}) {
  const titelId = id ? `${id}-titel` : undefined;
  return (
    <>
      {/* Preline: feedback-and-states/feedback-and-states-sub-alerts-recovery/feedback-and-states-cat-empty-states/no-events-empty-state-with-import-and-create-actions (verdichtet auf eine Zeile) */}
      <section id={id} aria-labelledby={titelId} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3 bg-card border border-card-line shadow-2xs rounded-xl">
        <h2 id={titelId} className="text-sm font-semibold text-foreground">
          {titel}
        </h2>
        <p className="text-sm text-muted-foreground-1" role="status">
          {text}
        </p>
        {aktion && (
          <Link
            className="sm:ms-auto text-sm text-primary hover:text-primary-hover focus:outline-hidden focus:decoration-2 focus:underline focus:text-primary-focus"
            href={aktion.href}
          >
            {aktion.text}
          </Link>
        )}
        {aktionsPlatz && <div className="w-full sm:w-auto sm:ms-auto">{aktionsPlatz}</div>}
      </section>
      {/* End Leerzeile */}
    </>
  );
}
